/**
 * SurfaceToggleHotkey
 *
 * Registers Option/Alt + Z to flip between the Operator surface (root) and
 * the Console (`/c/...`). Navigates via `recallPath` so each surface
 * resumes wherever the user last left it — same behavior as the
 * Operator / Console cross-link in the sidebar.
 *
 * Auth pages (`/signin`, `/sso`, `/oauth/...`) and dev preview routes are
 * deliberately skipped so the keystroke stays inert there — handy if you
 * actually want to type Ω.
 */

import { useHotkeys } from 'react-hotkeys-hook';
import { useLocation, useNavigate } from 'react-router-dom';
import { recallPath } from '@/lib/last-path';
import { routes } from '@/lib/routes';

const NON_TOGGLE_PREFIXES = [
  '/signin',
  '/signup',
  '/forgot-password',
  '/sso',
  '/oauth/',
  '/dev/',
];

export function SurfaceToggleHotkey() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useHotkeys(
    'alt+z',
    () => {
      const isConsole = pathname === '/c' || pathname.startsWith('/c/');
      const isOperator =
        !isConsole && !NON_TOGGLE_PREFIXES.some((p) => pathname.startsWith(p));
      if (!isConsole && !isOperator) return;
      navigate(
        isConsole
          ? recallPath('operator', routes.operator.root)
          : recallPath('console', routes.console.root),
      );
    },
    {
      description: 'Switch between Operator and Console',
      metadata: { group: 'General' },
      preventDefault: true,
    },
    [pathname, navigate],
  );

  return null;
}
